// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {GameActivityPresence} from '@app/features/game_activity/GameActivity';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {http} from '@app/features/platform/transport/RestTransport';

const logger = new Logger('GameHistoryReporter');
/** How often play time is reported while a game runs (server caps credit per report at 20 min). */
const REPORT_INTERVAL_MS = 5 * 60 * 1000;

let current: GameActivityPresence | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function send(activity: GameActivityPresence): void {
	if (!activity.startedAtMs) return;
	void http
		.post(Endpoints.USER_GAME_HISTORY_REPORT, {
			body: {
				steam_app_id: activity.steamAppId,
				name: activity.name.slice(0, 128),
				steam_icon_hash: activity.steamIconHash ?? null,
				session_started_at: activity.startedAtMs,
				played_ms: Math.max(0, Date.now() - activity.startedAtMs),
			},
		})
		.catch((error: unknown) => logger.debug('Game session report failed', error));
}

/** Called whenever the local game presence changes; reports the end of the old session and the start of the new one. */
export function trackGameSession(next: GameActivityPresence | null): void {
	const previous = current;
	const sameSession =
		previous && next && previous.steamAppId === next.steamAppId && previous.startedAtMs === next.startedAtMs;
	if (sameSession) {
		current = next;
		return;
	}
	if (previous) send(previous);
	if (timer) {
		clearInterval(timer);
		timer = null;
	}
	current = next;
	if (!next) return;
	send(next);
	timer = setInterval(() => {
		if (current) send(current);
	}, REPORT_INTERVAL_MS);
}
